/**
 * ScopeGapsPanel — Shows scopes blocked by missing prerequisites, bounds violations,
 * and expiring certifications.
 *
 * DRR-284: Prerequisite-Gated Scope Resolution + Certification Expiration Alerts
 * DRR-283: Scope Bounds Enforcement
 *
 * Displays:
 *   - Scopes blocked by missing prerequisites (training, delegation, etc.)
 *   - Scope bounds violations (floor/ceiling)
 *   - Certifications expiring soon with urgency levels
 *
 * Toggle with Ctrl+Shift+G or via the pill in bottom-left.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  ScrollArea,
  TextInput,
  ThemeIcon,
  Divider,
  Alert,
  Kbd,
  Accordion,
  Loader,
  Progress,
} from '@mantine/core';
import {
  IconShieldX,
  IconShieldOff,
  IconCheck,
  IconAlertTriangle,
  IconX,
  IconSearch,
  IconSchool,
  IconUserShare,
  IconCertificate,
  IconClock,
  IconEye,
  IconArrowDown,
  IconArrowUp,
  IconCalendarDue,
} from '@tabler/icons-react';

import { useAuth } from '@/auth/AuthContext';
import { useMyCertificationGapsQuery, CertificationUrgency } from '@/generated/graphql';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// Manual types until SDL is regenerated
interface PrerequisiteFailure {
  prerequisiteType: string;
  detail: string;
  remediation: string;
  remediationTaskGroup: string | null;
}

interface ScopeGap {
  scope: string;
  failures: PrerequisiteFailure[];
}

// DRR-283: Bounds violation types
interface BoundsViolation {
  violationType: 'FLOOR_MISSING' | 'CEILING_EXCEEDED';
  scope: string;
  roleId: string;
  message: string;
  statuteRef: string | null;
}

interface ScopeGapsResult {
  actorId: string;
  roles: string[];
  totalBlockedScopes: number;
  gaps: ScopeGap[];
  boundsViolations: BoundsViolation[];
  totalBoundsViolations: number;
}

const MY_SCOPE_GAPS_QUERY = gql`
  query MyScopeGaps {
    myScopeGaps {
      actorId
      roles
      totalBlockedScopes
      gaps {
        scope
        failures {
          prerequisiteType
          detail
          remediation
          remediationTaskGroup
        }
      }
      boundsViolations {
        violationType
        scope
        roleId
        message
        statuteRef
      }
      totalBoundsViolations
    }
  }
`;

// Prerequisite type to icon/color mapping
const PREREQUISITE_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  training: { color: 'blue', icon: <IconSchool size={14} />, label: 'Training Required' },
  delegation: { color: 'orange', icon: <IconUserShare size={14} />, label: 'Delegation Required' },
  certification: { color: 'violet', icon: <IconCertificate size={14} />, label: 'Certification Required' },
  tenure: { color: 'teal', icon: <IconClock size={14} />, label: 'Tenure Required' },
  witness: { color: 'pink', icon: <IconEye size={14} />, label: 'Witness Required' },
};

// Certification urgency to color/label mapping
const URGENCY_CONFIG: Record<string, { color: string; label: string; priority: number }> = {
  [CertificationUrgency.EXPIRED]: { color: 'red', label: 'Expired', priority: 0 },
  [CertificationUrgency.CRITICAL]: { color: 'red', label: 'Critical (<30d)', priority: 1 },
  [CertificationUrgency.WARNING]: { color: 'orange', label: 'Warning (<60d)', priority: 2 },
  [CertificationUrgency.NOTICE]: { color: 'yellow', label: 'Notice (<90d)', priority: 3 },
};

export function ScopeGapsPanel() {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('');

  const { tenant } = useAuth();

  // Query scope gaps
  // TODO: Switch to useMyScopeGapsQuery from generated/graphql after SDL regen
  const { data, loading, error } = useQuery<{ myScopeGaps: ScopeGapsResult }>(
    MY_SCOPE_GAPS_QUERY,
    {
      skip: !tenant?.id,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Query certification gaps (DRR-284)
  const {
    data: certData,
    loading: certLoading,
    error: certError,
  } = useMyCertificationGapsQuery({
    skip: !tenant?.id,
    fetchPolicy: 'cache-and-network',
  });

  const gaps = data?.myScopeGaps?.gaps ?? [];
  const totalBlocked = data?.myScopeGaps?.totalBlockedScopes ?? 0;
  const roles = data?.myScopeGaps?.roles ?? [];
  const boundsViolations = data?.myScopeGaps?.boundsViolations ?? [];
  const totalBoundsViolations = data?.myScopeGaps?.totalBoundsViolations ?? 0;

  // Certification gaps
  const expiringCerts = certData?.myCertificationGaps?.expiringCertifications ?? [];
  const totalExpiring = certData?.myCertificationGaps?.totalExpiring ?? 0;

  // Filter gaps by search term
  const filteredGaps = useMemo(() => {
    if (!filter) return gaps;
    const lowerFilter = filter.toLowerCase();
    return gaps.filter(
      gap =>
        gap.scope.toLowerCase().includes(lowerFilter) ||
        gap.failures.some(
          f =>
            f.prerequisiteType.toLowerCase().includes(lowerFilter) ||
            f.detail.toLowerCase().includes(lowerFilter) ||
            f.remediation.toLowerCase().includes(lowerFilter)
        )
    );
  }, [gaps, filter]);

  // Filter bounds violations by search term
  const filteredBoundsViolations = useMemo(() => {
    if (!filter) return boundsViolations;
    const lowerFilter = filter.toLowerCase();
    return boundsViolations.filter(
      v =>
        v.scope.toLowerCase().includes(lowerFilter) ||
        v.roleId.toLowerCase().includes(lowerFilter) ||
        v.message.toLowerCase().includes(lowerFilter) ||
        v.violationType.toLowerCase().includes(lowerFilter)
    );
  }, [boundsViolations, filter]);

  // Group failures by type for stats
  const stats = useMemo(() => {
    const counts: Record<string, number> = {
      training: 0,
      delegation: 0,
      certification: 0,
      tenure: 0,
      witness: 0,
    };
    for (const gap of gaps) {
      for (const failure of gap.failures) {
        const type = failure.prerequisiteType.toLowerCase();
        if (counts[type] !== undefined) {
          counts[type]++;
        }
      }
    }
    return counts;
  }, [gaps]);

  // Count bounds violations by type
  const boundsStats = useMemo(() => {
    let floorMissing = 0;
    let ceilingExceeded = 0;
    for (const v of boundsViolations) {
      if (v.violationType === 'FLOOR_MISSING') floorMissing++;
      else ceilingExceeded++;
    }
    return { floorMissing, ceilingExceeded };
  }, [boundsViolations]);

  // Filter expiring certifications by search term
  const filteredCerts = useMemo(() => {
    if (!filter) return expiringCerts;
    const lowerFilter = filter.toLowerCase();
    return expiringCerts.filter(
      c =>
        c.certType.toLowerCase().includes(lowerFilter) ||
        c.urgency.toLowerCase().includes(lowerFilter) ||
        c.affectedScopes.some(s => s.toLowerCase().includes(lowerFilter))
    );
  }, [expiringCerts, filter]);

  // Count certifications by urgency
  const certStats = useMemo(() => {
    const counts = {
      [CertificationUrgency.EXPIRED]: 0,
      [CertificationUrgency.CRITICAL]: 0,
      [CertificationUrgency.WARNING]: 0,
      [CertificationUrgency.NOTICE]: 0,
    };
    for (const cert of expiringCerts) {
      const key = cert.urgency as keyof typeof counts;
      if (key in counts) {
        counts[key]++;
      }
    }
    return counts;
  }, [expiringCerts]);

  // Keyboard shortcut: Ctrl+Shift+G
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'G') {
        e.preventDefault();
        setExpanded(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Total issues for pill badge
  const totalIssues = totalBlocked + totalBoundsViolations + totalExpiring;
  const hasUrgentCerts = (certStats[CertificationUrgency.EXPIRED] ?? 0) > 0 || (certStats[CertificationUrgency.CRITICAL] ?? 0) > 0;

  // Minimized pill
  if (!expanded) {
    return (
      <Tooltip label={<>Scope Gaps <Kbd size="xs">Ctrl+Shift+G</Kbd></>}>
        <Paper
          shadow="md"
          radius="xl"
          style={{
            position: 'fixed',
            bottom: 16,
            left: 140,
            zIndex: 1100,
            cursor: 'pointer',
            padding: '6px 14px',
          }}
          onClick={() => setExpanded(true)}
        >
          <Group gap="xs">
            <ThemeIcon size="xs" radius="xl" color={totalIssues > 0 ? (hasUrgentCerts ? 'red' : 'orange') : 'green'} variant="filled">
              {totalExpiring > 0 ? <IconCalendarDue size={10} /> : totalBoundsViolations > 0 ? <IconShieldOff size={10} /> : <IconShieldX size={10} />}
            </ThemeIcon>
            <Text size="xs" fw={600}>Scopes</Text>
            {totalIssues > 0 ? (
              <Badge size="xs" color={hasUrgentCerts ? 'red' : 'orange'}>
                {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
              </Badge>
            ) : (
              <Badge size="xs" color="green">
                All granted
              </Badge>
            )}
          </Group>
        </Paper>
      </Tooltip>
    );
  }

  // Expanded panel
  return (
    <Paper
      shadow="xl"
      radius="md"
      p="md"
      withBorder
      style={{
        position: 'fixed',
        bottom: 16,
        left: 140,
        width: 520,
        maxHeight: 'calc(100vh - 100px)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="sm" color="red" variant="light">
              <IconShieldX size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>Scope Gaps (DRR-284)</Text>
            <Kbd size="xs">Ctrl+Shift+G</Kbd>
          </Group>
          <ActionIcon variant="subtle" size="sm" onClick={() => setExpanded(false)}>
            <IconX size={14} />
          </ActionIcon>
        </Group>

        {/* Stats */}
        <Group gap="xs" wrap="wrap">
          <Badge size="sm" color={totalBlocked > 0 ? 'red' : 'green'} variant="light">
            {totalBlocked} blocked scope{totalBlocked !== 1 ? 's' : ''}
          </Badge>
          {Object.entries(stats).map(([type, count]) => {
            if (count === 0) return null;
            const config = PREREQUISITE_CONFIG[type] || { color: 'gray', label: type };
            return (
              <Badge key={type} size="sm" color={config.color} variant="light">
                {count} {config.label.toLowerCase().replace(' required', '')}
              </Badge>
            );
          })}
          {boundsStats.floorMissing > 0 && (
            <Badge size="sm" color="red" variant="light" leftSection={<IconArrowDown size={10} />}>
              {boundsStats.floorMissing} floor
            </Badge>
          )}
          {boundsStats.ceilingExceeded > 0 && (
            <Badge size="sm" color="orange" variant="light" leftSection={<IconArrowUp size={10} />}>
              {boundsStats.ceilingExceeded} ceiling
            </Badge>
          )}
          {totalExpiring > 0 && (
            <Badge size="sm" color={hasUrgentCerts ? 'red' : 'orange'} variant="light" leftSection={<IconCalendarDue size={10} />}>
              {totalExpiring} cert{totalExpiring !== 1 ? 's' : ''} expiring
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Search */}
        <TextInput
          size="xs"
          placeholder="Search scope, requirement, or remediation..."
          leftSection={<IconSearch size={14} />}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
        />

        {/* Current context */}
        <Alert color="blue" variant="light" p="xs">
          <Group gap="xs" wrap="wrap">
            <Text size="xs">Roles:</Text>
            {roles.length > 0 ? (
              roles.map(role => (
                <Badge key={role} size="xs" color="blue" variant="outline">
                  {role}
                </Badge>
              ))
            ) : (
              <Text size="xs" c="dimmed">none</Text>
            )}
          </Group>
        </Alert>

        {/* Gaps list */}
        <ScrollArea style={{ flex: 1, minHeight: 200 }}>
          {loading || certLoading ? (
            <Stack align="center" py="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Loading scope gaps...</Text>
            </Stack>
          ) : error || certError ? (
            <Alert color="red" variant="light">
              <Text size="sm">Failed to load: {error?.message || certError?.message}</Text>
            </Alert>
          ) : filteredGaps.length === 0 && filteredCerts.length === 0 ? (
            <Stack align="center" py="xl">
              <ThemeIcon size="xl" color="green" variant="light" radius="xl">
                <IconCheck size={24} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" ta="center">
                {filter ? 'No matching gaps found' : 'All prerequisites met! No blocked scopes.'}
              </Text>
            </Stack>
          ) : (
            <Accordion variant="contained" radius="sm">
              {filteredGaps.map((gap) => (
                <Accordion.Item key={gap.scope} value={gap.scope}>
                  <Accordion.Control>
                    <Group gap="xs">
                      <ThemeIcon size="sm" color="red" variant="light">
                        <IconShieldX size={14} />
                      </ThemeIcon>
                      <Text size="sm" ff="monospace" fw={500}>
                        {gap.scope}
                      </Text>
                      <Badge size="xs" color="red">
                        {gap.failures.length} prerequisite{gap.failures.length !== 1 ? 's' : ''} missing
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="xs">
                      {gap.failures.map((failure, idx) => {
                        const config = PREREQUISITE_CONFIG[failure.prerequisiteType.toLowerCase()] || {
                          color: 'gray',
                          icon: <IconAlertTriangle size={14} />,
                          label: failure.prerequisiteType,
                        };
                        return (
                          <Paper key={idx} p="xs" withBorder radius="sm">
                            <Stack gap={4}>
                              <Group gap="xs">
                                <ThemeIcon size="xs" color={config.color} variant="light">
                                  {config.icon}
                                </ThemeIcon>
                                <Badge size="xs" color={config.color}>
                                  {config.label}
                                </Badge>
                              </Group>
                              <Text size="xs" c="dimmed">
                                {failure.detail}
                              </Text>
                              <Divider my={4} />
                              <Group gap={4}>
                                <Text size="xs" fw={500} c="blue">
                                  Remediation:
                                </Text>
                                <Text size="xs">
                                  {failure.remediation}
                                </Text>
                              </Group>
                              {failure.remediationTaskGroup && (
                                <Badge size="xs" variant="outline" color="blue">
                                  Task Group: {failure.remediationTaskGroup}
                                </Badge>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </ScrollArea>

        {/* DRR-283: Bounds Violations Section */}
        {filteredBoundsViolations.length > 0 && (
          <>
            <Divider label="Scope Bounds Violations (DRR-283)" labelPosition="center" />
            <Alert color="orange" variant="light" p="xs" icon={<IconShieldOff size={16} />}>
              <Text size="xs">
                {totalBoundsViolations} statutory bounds violation{totalBoundsViolations !== 1 ? 's' : ''} detected.
                These indicate role misconfiguration.
              </Text>
            </Alert>
            <Accordion variant="contained" radius="sm">
              {filteredBoundsViolations.map((violation, idx) => {
                const isFloorMissing = violation.violationType === 'FLOOR_MISSING';
                return (
                  <Accordion.Item key={`${violation.roleId}-${violation.scope}-${idx}`} value={`bounds-${idx}`}>
                    <Accordion.Control>
                      <Group gap="xs">
                        <ThemeIcon size="sm" color={isFloorMissing ? 'red' : 'orange'} variant="light">
                          {isFloorMissing ? <IconArrowDown size={14} /> : <IconArrowUp size={14} />}
                        </ThemeIcon>
                        <Text size="sm" ff="monospace" fw={500}>
                          {violation.scope}
                        </Text>
                        <Badge size="xs" color={isFloorMissing ? 'red' : 'orange'}>
                          {isFloorMissing ? 'Floor Missing' : 'Ceiling Exceeded'}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        <Paper p="xs" withBorder radius="sm">
                          <Stack gap={4}>
                            <Group gap="xs">
                              <Text size="xs" fw={500}>Role:</Text>
                              <Badge size="xs" variant="outline">{violation.roleId}</Badge>
                            </Group>
                            <Text size="xs" c="dimmed">{violation.message}</Text>
                            {violation.statuteRef && (
                              <Group gap={4}>
                                <Text size="xs" fw={500} c="blue">Statute:</Text>
                                <Text size="xs" ff="monospace">{violation.statuteRef}</Text>
                              </Group>
                            )}
                            <Divider my={4} />
                            <Text size="xs" c={isFloorMissing ? 'red' : 'orange'}>
                              {isFloorMissing
                                ? 'This scope is required by statute but missing from the role.'
                                : 'This scope exceeds statutory authority and will be filtered.'}
                            </Text>
                          </Stack>
                        </Paper>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </>
        )}

        {/* DRR-284: Expiring Certifications Section */}
        {filteredCerts.length > 0 && (
          <>
            <Divider label="Expiring Certifications (DRR-284)" labelPosition="center" />
            <Alert color={hasUrgentCerts ? 'red' : 'orange'} variant="light" p="xs" icon={<IconCalendarDue size={16} />}>
              <Text size="xs">
                {totalExpiring} certification{totalExpiring !== 1 ? 's' : ''} expiring.
                {hasUrgentCerts && ' Some scopes are blocked until renewal.'}
              </Text>
            </Alert>
            <Accordion variant="contained" radius="sm">
              {filteredCerts.map((cert, idx) => {
                const urgencyConfig = URGENCY_CONFIG[cert.urgency] || { color: 'gray', label: cert.urgency, priority: 99 };
                const isExpired = cert.urgency === CertificationUrgency.EXPIRED;
                const daysText = isExpired
                  ? `Expired ${Math.abs(cert.daysUntilExpiration)} day${Math.abs(cert.daysUntilExpiration) !== 1 ? 's' : ''} ago`
                  : `${cert.daysUntilExpiration} day${cert.daysUntilExpiration !== 1 ? 's' : ''} remaining`;

                return (
                  <Accordion.Item key={`${cert.certType}-${idx}`} value={`cert-${idx}`}>
                    <Accordion.Control>
                      <Group gap="xs">
                        <ThemeIcon size="sm" color={urgencyConfig.color} variant="light">
                          <IconCertificate size={14} />
                        </ThemeIcon>
                        <Text size="sm" fw={500}>
                          {cert.certType}
                        </Text>
                        <Badge size="xs" color={urgencyConfig.color}>
                          {urgencyConfig.label}
                        </Badge>
                        <Text size="xs" c="dimmed">{daysText}</Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        <Paper p="xs" withBorder radius="sm">
                          <Stack gap={4}>
                            <Group gap="xs">
                              <Text size="xs" fw={500}>Expires:</Text>
                              <Text size="xs" ff="monospace">{cert.expiresAt}</Text>
                            </Group>
                            {cert.affectedScopes.length > 0 && (
                              <>
                                <Divider my={4} />
                                <Text size="xs" fw={500}>Affected Scopes:</Text>
                                <Group gap={4} wrap="wrap">
                                  {cert.affectedScopes.map(scope => (
                                    <Badge key={scope} size="xs" variant="outline" color={isExpired ? 'red' : 'orange'}>
                                      {scope}
                                    </Badge>
                                  ))}
                                </Group>
                              </>
                            )}
                            <Divider my={4} />
                            <Text size="xs" c={isExpired ? 'red' : 'orange'}>
                              {isExpired
                                ? 'This certification has expired. Renew to restore affected scopes.'
                                : 'Renew before expiration to maintain scope access.'}
                            </Text>
                          </Stack>
                        </Paper>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </>
        )}

        {/* Help text */}
        <Text size="xs" c="dimmed">
          {totalBlocked > 0 && 'Blocked scopes require prerequisites (DRR-284). '}
          {totalBoundsViolations > 0 && 'Bounds violations indicate role config issues (DRR-283). '}
          {totalExpiring > 0 && 'Expiring certifications affect scope access. '}
          {totalIssues === 0 && 'All scopes granted. No issues detected.'}
        </Text>
      </Stack>
    </Paper>
  );
}
